import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Process = () => {
  const { t } = useTranslation('home');
  const [activePhase, setActivePhase] = useState('discovery');

  const phaseKeys = ['discovery', 'design', 'development', 'testing', 'launch'];

  // eslint-disable-next-line no-unused-vars
  const phaseGraphics = {
    discovery: (
      <svg className="w-full h-full" viewBox="0 0 160 160" fill="none">
        {/* Outer circle */}
        <circle cx="80" cy="80" r="55" stroke="black" strokeWidth="1.5" fill="none" opacity="0.3" />
        {/* Search beam effect */}
        <circle cx="80" cy="80" r="45" stroke="black" strokeWidth="2" fill="none" strokeDasharray="4 6" opacity="0.5" />
        {/* Center focus */}
        <circle cx="80" cy="80" r="30" stroke="black" strokeWidth="2" fill="none" />
        {/* Inner detail */}
        <rect x="65" y="65" width="30" height="30" stroke="black" strokeWidth="1.5" fill="none" opacity="0.6" />
        {/* Accent elements */}
        <circle cx="100" cy="60" r="4" fill="black" opacity="0.4" />
        <circle cx="110" cy="100" r="3" fill="black" opacity="0.5" />
        <circle cx="55" cy="95" r="3.5" fill="black" opacity="0.4" />
        {/* Connection lines */}
        <line x1="80" y1="50" x2="80" y2="35" stroke="black" strokeWidth="1.5" opacity="0.5" strokeLinecap="round" />
        <line x1="110" y1="80" x2="125" y2="80" stroke="black" strokeWidth="1.5" opacity="0.5" strokeLinecap="round" />
      </svg>
    ),
    design: (
      <svg className="w-full h-full" viewBox="0 0 160 160" fill="none">
        {/* Monitor frame */}
        <rect x="25" y="30" width="110" height="85" rx="3" stroke="black" strokeWidth="2" fill="none" />
        {/* Screen area */}
        <rect x="30" y="35" width="100" height="75" stroke="black" strokeWidth="0.5" fill="none" opacity="0.3" />
        {/* Grid elements on screen */}
        <rect x="35" y="40" width="90" height="12" rx="1" stroke="black" strokeWidth="0.5" fill="black" opacity="0.08" />
        <rect x="35" y="58" width="18" height="35" rx="1" fill="black" opacity="0.12" />
        <rect x="58" y="58" width="67" height="8" rx="1" fill="black" opacity="0.08" />
        <rect x="58" y="71" width="67" height="8" rx="1" fill="black" opacity="0.08" />
        <rect x="58" y="84" width="40" height="8" rx="1" fill="black" opacity="0.08" />
        {/* Stand */}
        <rect x="68" y="115" width="24" height="12" rx="2" stroke="black" strokeWidth="1.5" fill="none" />
        {/* Accent dots */}
        <circle cx="125" cy="48" r="2.5" fill="black" opacity="0.4" />
        <circle cx="130" cy="72" r="2" fill="black" opacity="0.3" />
      </svg>
    ),
    development: (
      <svg className="w-full h-full" viewBox="0 0 160 160" fill="none">
        {/* Combined web and mobile elements */}
        {/* Web browser window */}
        <rect x="15" y="20" width="80" height="60" rx="3" stroke="black" strokeWidth="1.5" fill="none" />
        <rect x="18" y="23" width="74" height="8" rx="1" fill="black" opacity="0.1" />
        <rect x="18" y="35" width="74" height="3" rx="0.5" fill="black" opacity="0.08" />
        <rect x="18" y="42" width="60" height="3" rx="0.5" fill="black" opacity="0.08" />
        <rect x="18" y="49" width="50" height="3" rx="0.5" fill="black" opacity="0.08" />

        {/* Mobile phone */}
        <rect x="105" y="25" width="35" height="70" rx="5" stroke="black" strokeWidth="1.5" fill="none" />
        <rect x="108" y="30" width="29" height="55" rx="2" stroke="black" strokeWidth="0.5" fill="none" opacity="0.3" />
        <rect x="110" y="35" width="25" height="6" rx="1" fill="black" opacity="0.12" />
        <rect x="110" y="45" width="25" height="3" rx="0.5" fill="black" opacity="0.08" />
        <rect x="110" y="52" width="20" height="3" rx="0.5" fill="black" opacity="0.08" />
        <circle cx="117.5" cy="75" r="2" stroke="black" strokeWidth="1" fill="none" opacity="0.4" />

        {/* Connection lines between web and mobile */}
        <line x1="95" y1="50" x2="105" y2="50" stroke="black" strokeWidth="1" opacity="0.4" strokeLinecap="round" />
        <circle cx="95" cy="50" r="1.5" fill="black" opacity="0.3" />
        <circle cx="105" cy="50" r="1.5" fill="black" opacity="0.3" />

        {/* Code elements */}
        <rect x="25" y="95" width="40" height="2" rx="1" fill="black" opacity="0.4" />
        <rect x="25" y="100" width="35" height="2" rx="1" fill="black" opacity="0.3" />
        <rect x="25" y="105" width="45" height="2" rx="1" fill="black" opacity="0.3" />

        {/* Accent elements */}
        <circle cx="130" cy="110" r="2" fill="black" opacity="0.3" />
        <circle cx="35" cy="125" r="1.5" fill="black" opacity="0.4" />
      </svg>
    ),
    testing: (
      <svg className="w-full h-full" viewBox="0 0 160 160" fill="none">
        {/* Outer check circle */}
        <circle cx="80" cy="80" r="50" stroke="black" strokeWidth="2" fill="none" />
        {/* Check mark path with varying strokes */}
        <path d="M 58 82 L 73 97 L 108 62" stroke="black" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {/* Secondary validation dots */}
        <circle cx="100" cy="52" r="3" fill="black" opacity="0.3" />
        <circle cx="115" cy="75" r="2.5" fill="black" opacity="0.4" />
        <circle cx="50" cy="110" r="2.5" fill="black" opacity="0.3" />
        {/* Subtle progress bar */}
        <rect x="50" y="125" width="60" height="2" rx="1" stroke="black" strokeWidth="0.5" fill="none" opacity="0.4" />
        <rect x="50" y="125" width="45" height="2" rx="1" fill="black" opacity="0.3" />
      </svg>
    ),
    launch: (
      <svg className="w-full h-full" viewBox="0 0 160 160" fill="none">
        {/* Rocket body */}
        <path d="M 80 30 L 85 60 L 80 65 L 75 60 Z" stroke="black" strokeWidth="2" fill="black" opacity="0.2" strokeLinejoin="round" />
        {/* Rocket middle section */}
        <rect x="70" y="65" width="20" height="35" stroke="black" strokeWidth="2" fill="none" rx="2" />
        {/* Rocket fins */}
        <path d="M 70 85 L 55 100 L 60 85 Z" stroke="black" strokeWidth="1.5" fill="black" opacity="0.15" />
        <path d="M 90 85 L 105 100 L 100 85 Z" stroke="black" strokeWidth="1.5" fill="black" opacity="0.15" />
        {/* Flame/exhaust */}
        <path d="M 75 105 L 72 125 L 80 110 L 88 125 L 85 105" stroke="black" strokeWidth="1.5" fill="none" opacity="0.6" strokeLinecap="round" strokeLinejoin="round" />
        {/* Accent dots for motion */}
        <circle cx="75" cy="115" r="2" fill="black" opacity="0.3" />
        <circle cx="85" cy="118" r="1.5" fill="black" opacity="0.4" />
        {/* Window */}
        <circle cx="80" cy="75" r="3" stroke="black" strokeWidth="1" fill="none" opacity="0.5" />
      </svg>
    ),
  };

  return (
    <section className="bg-[#FFFFFF] min-h-screen py-12 pb-18 mb-20 flex items-center" data-section="process">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col-reverse md:flex-row justify-between items-start gap-6 mb-16"
        >
          <div className="text-left max-w-md">
            <p className="text-base sm:text-lg text-grey-accent">
              {t('process.description')}
            </p>
          </div>
          <div className="text-left md:text-right">
            <p className="text-secondary-accent text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2 md:justify-end">
              <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
              {t('process.badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-text-primary">
              {t('process.title')}
            </h2>
          </div>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 relative">
          {/* Left Sidebar - Timeline */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-4"
          >
            <div className="relative">
              {/* Vertical Line through timeline - REMOVED, using extending line instead */}

              <div className="space-y-0 relative z-20">
                {phaseKeys.map((phaseKey, index) => (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActivePhase(phaseKey)}
                    className={`w-full text-left py-4 transition-all duration-300 flex items-center gap-6 group relative z-20 ${activePhase === phaseKey ? '' : ''
                      }`}
                  >
                    {/* Code-style Indicator with dark gray background */}
                    <div className="flex items-center gap-2 flex-shrink-0 relative z-10">
                      <span className={`font-mono text-base transition-colors duration-300 bg-[#2A2A2A] text-white px-3 py-1.5 ${activePhase === phaseKey
                        ? 'font-bold'
                        : 'group-hover:bg-text-primary'
                        }`}>
                        {'//' + (index + 1) + ':'}
                      </span>
                    </div>

                    {/* Phase Title */}
                    <div className="flex-1">
                      <h3 className={`text-base lg:text-lg font-semibold transition-colors duration-300 ${activePhase === phaseKey
                        ? 'text-text-primary'
                        : 'text-grey-accent group-hover:text-text-primary'
                        }`}>
                        {t(`process.phases.${phaseKey}.title`)}
                      </h3>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Vertical Separator */}
          <div className="hidden lg:block absolute left-1/3 top-0 bottom-0 w-px bg-[#D0D0D0]"></div>

          {/* Connecting line that extends down to Contact section */}
          <motion.div
            className="hidden lg:block absolute w-1 bg-[#2A2A2A]"
            style={{ left: '29px', top: '0', height: '320px', zIndex: 10, transformOrigin: 'top center' }}
            initial={{ opacity: 0, scaleY: 0 }}
            whileInView={{ opacity: 1, scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.0, delay: 0.8, ease: "easeOut" }}
          ></motion.div>

          {/* Right Content - Phase Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-8"
          >
            <AnimatePresence mode="wait">
              {phaseKeys.map((phaseKey, phaseIndex) => {
                if (activePhase !== phaseKey) return null;

                const points = t(`process.phases.${phaseKey}.points`, { returnObjects: true });

                return (
                  <motion.div
                    key={phaseKey}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 1 }}
                    transition={{ duration: 0 }}
                    className="relative"
                  >
                    {/* Card - Fixed height on desktop, auto on mobile */}
                    <div className="bg-white rounded-2xl p-6 sm:p-8 relative overflow-hidden min-h-[320px] lg:h-[400px] flex flex-col shadow-sm">
                      {/* Content */}
                      <div className="relative z-10 flex-1 flex flex-col">
                        {/* Title */}
                        <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-3">
                          {t(`process.phases.${phaseKey}.title`)}
                        </h3>

                        {/* Intro */}
                        <p className="text-lg text-grey-accent mb-6 font-medium">
                          {t(`process.phases.${phaseKey}.intro`)}
                        </p>

                        {/* Key Points - Always same layout */}
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-text-primary uppercase tracking-wider mb-4 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-secondary-accent rounded-full"></span>
                            {t('process.keyActivities')}
                          </h4>
                          <ul className="space-y-3">
                            {Array.isArray(points) && points.map((point, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-grey-accent">
                                <span className="text-text-primary font-bold mt-0.5 flex-shrink-0">—</span>
                                <span className="text-base leading-relaxed">{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Process;
